import { useState, useEffect, useContext, type ReactElement } from 'react'
import { Grid, Box, Button } from '@mui/material'
import { Body2White, Body1White } from '@/components/typography'
import css from './styles.module.css'
// import { useAppSelector, type RootState } from '@/store'
// import { useVisibleBalances } from '@/hooks/useVisibleBalances'
// import { selectCurrency } from '@/store/settingsSlice'
// import { formatCurrency } from '@/utils/formatNumber'
import EthHashInfo from '@/components/common/EthHashInfo'
// import { type Inheritor, RuleType } from '@/store/setUpInheritorsSlice'
// import { SafeTxContext } from '@/components/tx-flow/SafeTxProvider'
import { ModalButtonContext } from '@/components/common/ModalButton'
import { encodeNewInheritorsFunc } from '@/utils/inherit-manager'
// import useGasLimit from '@/hooks/useGasLimit'
// import { useCurrentChain } from '@/hooks/useChains'
// import { createTx } from '@/services/tx/tx-sender'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import dayjs from 'dayjs'
// import useSafeAddress from '@/hooks/useSafeAddress'
// import { useAdvancedParams } from '@/components/tx/AdvancedParams'
// import { useTxActions } from '@/components/tx/SignOrExecuteForm/hooks'
import { PendingStatus } from '@/store/pendingTxsSlice'
import useChainId from '@/hooks/useChainId'
import { SendProcessingContext } from '@/components/inheritors/SendProcessing'
import { useRouter } from 'next/router'
import { RuleType, type Inheritor, type InheritanceStep, type RuleInfo } from '@/store/setUpInheritorsV2Slice'
import { useAppDispatch } from '@/store'
import { upsertAddressBookEntry } from '@/store/addressBookV2Slice'
import RuleRender from '@/components/inheritors/RuleRender'
import { trackEvent, INHERIT_EVENTS } from '@/services/analytics'

dayjs.extend(utc)
dayjs.extend(timezone)

const ConfirmInheritance = ({ inheritanceStep }: { inheritanceStep: InheritanceStep }): ReactElement => {
  const dispatch = useAppDispatch()

  const [rule, setRule] = useState<RuleInfo>(inheritanceStep.step2.rules)
  // const [sendError, setSendError] = useState('')
  // const [txId, setTxId] = useState<string | undefined>(undefined)
  // const [txHash, setTxHash] = useState<string | undefined>(undefined)
  // const [txStatus, setTxStatus] = useState<PendingStatus | undefined>(undefined)

  // const dispatch = useAppDispatch()
  const inheritanceInfo = inheritanceStep
  // const currency = useAppSelector(selectCurrency)
  // const { balances, loading: balancesLoading } = useVisibleBalances()
  // const fiatTotal = useMemo(
  //   () => (balances.fiatTotal ? formatCurrency(balances.fiatTotal, currency) : ''),
  //   [currency, balances.fiatTotal],
  // )
  // const numTokens = useMemo(() => balances.items.filter((token) => token.balance !== '0').length, [balances])
  // const [isSubmitting, setIsSubmitting] = useState(false)

  // const { setSafeTx, setSafeTxError, safeTx } = useContext(SafeTxContext)
  const { setModalOpen, setTitle } = useContext(ModalButtonContext)

  // Estimate gas limit
  // const { gasLimit, gasLimitError } = useGasLimit(safeTx)
  // const [advancedParams, setAdvancedParams] = useAdvancedParams(gasLimit)
  // const chainInfo = useCurrentChain()

  // const safeAddress = useSafeAddress()

  // const { executeTx } = useTxActions()
  const chainId = useChainId()
  const router = useRouter()

  const { isSending, sendTx, sendError, txStatus } = useContext(SendProcessingContext)

  useEffect(() => {
    if (isSending) {
      if (txStatus && txStatus === PendingStatus.INDEXING) {
        router.push({ pathname: '/home', query: { wallet: router.query.wallet } })
        setModalOpen(false)
        return
      }
    }
  }, [txStatus])

  useEffect(() => {
    const { rules } = inheritanceInfo.step2
    const { inheritors } = inheritanceInfo.step1
    if (rules.type === RuleType.OnSpecificDate) {
      const newRule = { ...rules, timestamp: Math.floor(rules.timestamp / 1000) }
      setRule(newRule)
    }
  }, [inheritanceInfo])

  const handleBack = () => {
    setModalOpen(false)
  }

  const handleConfirm = async () => {
    trackEvent(INHERIT_EVENTS.CLICK_CONFIRM_SET_UP_INHERITANCE)
    // add to address book
    inheritanceInfo.step1.inheritors.forEach((inheritor) => {
      dispatch(
        upsertAddressBookEntry({
          chainId: chainId,
          address: inheritor.address,
          name: inheritor.name,
          email: inheritor.email,
        }),
      )
    })

    const txParams = encodeNewInheritorsFunc(inheritanceInfo.step1.inheritors, inheritanceInfo.step2.rules, chainId)
    void sendTx(txParams, true)
  }

  const renderInheritor = (inheritor: Inheritor, index: number) => {
    return (
      <Grid key={index} item px="16px" py="8px" sx={{ boxShadow: '0px 1px 0px 0px #2F354B' }}>
        <Grid container py="2px">
          <Grid item xs={4} sm={2}>
            <Body2White>Name:</Body2White>
          </Grid>
          <Grid item xs={8} sm={10}>
            <Body2White sx={{ color: 'var(--Ebony-Clay-300, #D2D7E5)' }}>
              {inheritor.name || `Inheritor ${index + 1}`}
            </Body2White>
          </Grid>
        </Grid>
        <Grid container py="2px">
          <Grid item xs={4} sm={2}>
            <Body2White>Address:</Body2White>
          </Grid>
          <Grid item xs={8} sm={10}>
            <Body1White sx={{ color: 'var(--Blue-Ribbon-600, #5D5DFF)' }}>
              <EthHashInfo
                address={inheritor.address}
                showAvatar={false}
                showName={false}
                hasExplorer={false}
                shortAddress={false}
                shortenLen={6}
              />
            </Body1White>
          </Grid>
        </Grid>
        <Grid container py="2px">
          <Grid item xs={4} sm={2}>
            <Body2White>Percentage:</Body2White>
          </Grid>
          <Grid item xs={8} sm={10}>
            <Body1White sx={{ color: 'var(--Emerald-600, #22C55E)' }}>{inheritor.percentage}%</Body1White>
          </Grid>
        </Grid>
        <Grid container py="2px">
          <Grid item xs={4} sm={2}>
            <Body2White>Email:</Body2White>
          </Grid>
          <Grid item xs={8} sm={10}>
            <Body2White
              sx={{ color: inheritor.email ? 'var(--Ebony-Clay-300, #D2D7E5)' : 'var(--Ebony-Clay-700, #485581)' }}
            >
              {inheritor.email || '-- empty --'}
            </Body2White>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  const renderFooter = () => {
    return (
      <Grid item xs={12} mt="16px">
        <Grid container>
          <Grid item xs={5} sm={4}>
            <Button
              // disabled={isSubmitting}
              variant="text"
              sx={{
                paddingY: '10px',
                width: '100%',
                borderRadius: '4px',
                background: 'var(--Ebony-Clay-950, #2F354B)',
                color: 'var(--Ebony-Clay-300, #D2D7E5)',
                ':hover': {
                  background: 'rgba(255, 255, 255, 0.08)',
                },
              }}
              onClick={handleBack}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item xs={2} sm={4}></Grid>
          <Grid item xs={5} sm={4} textAlign="right">
            <Button
              // disabled={isSubmitting}
              variant="contained"
              sx={{
                paddingY: '10px',
                width: '100%',
              }}
              onClick={handleConfirm}
            >
              Confirm
            </Button>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <Grid
          container
          mt="16px"
          mb="16px"
          sx={{
            borderRadius: '4px',
            border: '1px solid var(--Ebony-Clay-950, #2F354B)',
          }}
        >
          <Box sx={{ width: '100%' }}>
            <Grid item px="16px" py="8px" sx={{ background: 'var(--Supporting-Dark-BG-700, #272A3E)' }}>
              {/* {ruleMessage} */}
              <RuleRender rule={rule} numInheritors={inheritanceInfo.step1.inheritors.length} />
            </Grid>
            <Box className={css.confirmInheritanceScroll} sx={{ width: '100%' }}>
              {inheritanceInfo.step1.inheritors.map((inheritor, index) => renderInheritor(inheritor, index))}
            </Box>
          </Box>
        </Grid>
      </Grid>
      {sendError !== '' && <Body2White sx={{ color: 'var(--Supporting-Carnation, #FF2239)' }}>{sendError}</Body2White>}
      {renderFooter()}
    </Grid>
  )
}

const highlightGreenText = (text: string) => {
  return <span style={{ color: 'var(--Emerald-600, #22C55E)', fontWeight: 'bold' }}>{text}</span>
}

export default ConfirmInheritance
